import { Alert, Anchor, Box, Button, Group, Image, Loader, PasswordInput, Stack, Text, TextInput } from '@mantine/core'
import { notifications } from '@mantine/notifications'
import React from 'react'
import { useOwlbertWebAuth } from './auth/OwlbertWebAuthContext'

type Mode = 'signin' | 'signup'

/** Minimum enforced by the backend (`z.string().min(6)` on both signup paths). */
const MIN_PASSWORD_LENGTH = 6

/**
 * Full-screen sign-in / sign-up (no app chrome). Uses the waving Owlbert asset from `ui/public`.
 *
 * Signup lives here as well as on the marketing site: this app is on a different origin, so an
 * account created elsewhere still has to be signed into here, and sending people off-site to
 * create one was a dead end.
 */
export const WebLoginPage: React.FC = () => {
  const auth = useOwlbertWebAuth()
  const [mode, setMode] = React.useState<Mode>('signin')
  const [email, setEmail] = React.useState('')
  const [password, setPassword] = React.useState('')
  const [confirmPassword, setConfirmPassword] = React.useState('')
  const [busy, setBusy] = React.useState(false)
  const [confirmationSent, setConfirmationSent] = React.useState(false)

  const isSignup = mode === 'signup'
  // `signupAvailable` is null until the query resolves (or if it failed); don't hide the option then.
  const signupOffered = auth.signupAvailable !== false

  const switchMode = (next: Mode) => {
    setMode(next)
    setPassword('')
    setConfirmPassword('')
    setConfirmationSent(false)
  }

  const validate = (): string | null => {
    if (!email.trim()) return 'Email is required.'
    if (!password) return 'Password is required.'
    if (!isSignup) return null
    if (password.length < MIN_PASSWORD_LENGTH) return `Password must be at least ${MIN_PASSWORD_LENGTH} characters.`
    if (password !== confirmPassword) return 'Passwords do not match.'
    return null
  }

  const onSubmit = async () => {
    if (!auth.configured) {
      notifications.show({ color: 'yellow', message: 'Sign-in is not available in this build.' })
      return
    }
    const problem = validate()
    if (problem) {
      notifications.show({ color: 'yellow', message: problem })
      return
    }

    setBusy(true)
    try {
      if (isSignup) {
        const result = await auth.signup(email.trim(), password)
        if (result.status === 'confirmation_required') {
          setConfirmationSent(true)
          notifications.show({ color: 'blue', message: 'Account created. Check your email to confirm it.' })
        } else {
          notifications.show({ color: 'green', message: 'Account created. You are signed in.' })
        }
      } else {
        await auth.login(email.trim(), password)
        notifications.show({ color: 'green', message: 'Signed in.' })
      }
      setPassword('')
      setConfirmPassword('')
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e)
      notifications.show({
        color: 'red',
        title: isSignup ? 'Sign-up failed' : 'Sign-in failed',
        message,
      })
    } finally {
      setBusy(false)
    }
  }

  const submitOnEnter = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') void onSubmit()
  }

  return (
    <Box
      style={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 24,
        boxSizing: 'border-box',
        background: 'var(--mantine-color-body)',
      }}
    >
      <Stack align="center" gap="lg" maw={400} w="100%">
        <Image
          src="/owlbert-wave-box.png"
          alt="Owlbert waving"
          w={280}
          mah={320}
          fit="contain"
          draggable={false}
        />
        <Stack gap="sm" w="100%">
          {auth.error ? (
            <Alert color="red" title={isSignup ? 'Sign-up' : 'Sign-in'}>
              {auth.error}
            </Alert>
          ) : null}
          {confirmationSent ? (
            <Alert color="blue" title="Confirm your email">
              We sent a confirmation link to {email.trim()}. Open it, then sign in here.
            </Alert>
          ) : null}
          {auth.loading ? (
            <Stack align="center" gap="xs" py="md">
              <Loader size="sm" />
              <Text size="sm" c="dimmed">
                Checking session…
              </Text>
            </Stack>
          ) : (
            <Stack gap="sm">
              <TextInput
                label="Email"
                type="email"
                autoComplete="username"
                value={email}
                onChange={(e) => setEmail(e.currentTarget.value)}
                disabled={busy || !auth.configured}
                onKeyDown={submitOnEnter}
              />
              <PasswordInput
                label="Password"
                autoComplete={isSignup ? 'new-password' : 'current-password'}
                description={isSignup ? `At least ${MIN_PASSWORD_LENGTH} characters` : undefined}
                value={password}
                onChange={(e) => setPassword(e.currentTarget.value)}
                disabled={busy || !auth.configured}
                onKeyDown={submitOnEnter}
              />
              {isSignup ? (
                <PasswordInput
                  label="Confirm password"
                  autoComplete="new-password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.currentTarget.value)}
                  disabled={busy || !auth.configured}
                  onKeyDown={submitOnEnter}
                />
              ) : null}
              <Button
                className="accent-button"
                fullWidth
                mt="xs"
                loading={busy}
                disabled={!auth.configured}
                onClick={() => void onSubmit()}
              >
                {isSignup ? 'Create account' : 'Sign in'}
              </Button>

              {auth.signupAvailable === false ? (
                <Text size="sm" c="dimmed" ta="center">
                  New signups are currently closed.
                </Text>
              ) : null}

              {signupOffered ? (
                <Group justify="center" gap={6} mt="xs">
                  <Text size="sm" c="dimmed">
                    {isSignup ? 'Already have an account?' : "Don't have an account?"}
                  </Text>
                  <Anchor
                    size="sm"
                    component="button"
                    type="button"
                    disabled={busy}
                    onClick={() => switchMode(isSignup ? 'signin' : 'signup')}
                  >
                    {isSignup ? 'Sign in' : 'Create one'}
                  </Anchor>
                </Group>
              ) : null}
            </Stack>
          )}
        </Stack>
      </Stack>
    </Box>
  )
}
